/**
 * ProjectEditDialog — 项目信息 Tab
 */
import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { projectApi, getErrorMessage } from '../services/api';

interface ProjectInfoTabProps {
  projectId: number;
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  descriptiveLevel: string;
  setDescriptiveLevel: (v: string) => void;
  onSave: () => void;
}

type AiTarget = 'description' | null;

export default function ProjectInfoTab({
  projectId, name, setName, description, setDescription,
  descriptiveLevel, setDescriptiveLevel, onSave,
}: ProjectInfoTabProps) {
  const [aiTarget, setAiTarget] = useState<AiTarget>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleAiDescription = async () => {
    if (!name.trim()) {
      setAiError('请先填写项目名称');
      return;
    }
    setAiTarget('description');
    setAiError(null);
    setDescription('');
    try {
      const full = await projectApi.generateProjectDescriptionStream(
        projectId,
        name.trim(),
        (fullText) => setDescription(fullText),
      );
      setDescription(full.trim());
    } catch (err) {
      setAiError(getErrorMessage(err, 'AI 生成描述失败'));
    } finally {
      setAiTarget(null);
    }
  };

  const aiBtnCls =
    'inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50';

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">项目名称</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="请输入项目名称" onKeyDown={(e) => e.key === 'Enter' && onSave()} autoFocus />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">项目描述</label>
          <button
            type="button"
            onClick={() => void handleAiDescription()}
            disabled={aiTarget !== null}
            className={aiBtnCls}
            title="AI 根据项目名称生成描述"
          >
            <SparklesIcon className="w-3.5 h-3.5 mr-1" />
            {aiTarget === 'description' ? '生成中...' : 'AI 生成'}
          </button>
        </div>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          placeholder="项目描述（可选）" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">提纲描述详细度</label>
        <select value={descriptiveLevel} onChange={(e) => setDescriptiveLevel(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
          <option value="simple">简单 — 1-2句话概括</option>
          <option value="detailed">详细 — 2-4句话说明（推荐）</option>
          <option value="very_detailed">非常详细 — 4-6句话展开</option>
        </select>
      </div>

      {aiError && (
        <div className="px-3 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
          {aiError}
        </div>
      )}
    </div>
  );
}
